import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Sentry from '@sentry/angular';
import { combineLatest, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthFacade } from '../../auth/store/auth.facade';
import { getCurrentEnvironmentName } from '../../shared/helpers/get-current-environment-name/get-current-environment-name.helper';
import { isNullOrUndefined } from '../../shared/helpers/is-null-or-undefined/is-null-or-undefined.helper';
import { SharedFacade } from '../../shared/store/shared.facade';

@Injectable({ providedIn: 'root' })
export class SentryService {
  readonly #authFacade = inject(AuthFacade);
  readonly #sharedFacade = inject(SharedFacade);

  readonly #userTracking$ = combineLatest([
    this.#authFacade.user$,
    this.#sharedFacade.accountId$,
    this.#sharedFacade.accountName$,
  ]).pipe(
    tap(([user, accountId, accountName]) => {
      if (isNullOrUndefined(user)) {
        Sentry.setUser(null);
        return;
      }

      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        companyId: user.company?.id,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        companyName: user.company?.name,
        accountId,
        accountName,
      });
    }),
  );

  constructor() {
    if (!environment.useSentry) {
      return;
    }

    Sentry.init({
      environment: getCurrentEnvironmentName(),
      dsn: 'https://29fc41cf825a9e861b6fb4ed63e095a6@o4511183403941888.ingest.de.sentry.io/4511183405776976',
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        Sentry.browserTracingIntegration(),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      tracesSampleRate: 0.1,
    });

    this.#userTracking$.pipe(takeUntilDestroyed()).subscribe();
  }
}
